import { useQueryClient } from '@tanstack/react-query'

import { useModal } from 'lib/components/Modal/Modal'
import { SiteHeaderProps } from 'lib/components/SiteHeader/SiteHeader'

import { useUserApi } from '@/api/useUserApi'
import {
  SITE_HEADER_BRANDS,
  SITE_HEADER_BUTTON,
  SITE_HEADER_HELP_LINKS,
  SITE_HEADER_LINKS,
} from '@/components/siteHeaderConfig'
import { useMounted } from '@/hooks/usePageLoad'
import useSessionStore from '@/stores/useSessionStore'
import { redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'
import { brand } from '@/utils/brand'

export const useSiteHeader = (type: SiteHeaderProps['type'] = 'primary'): SiteHeaderProps => {
  const mounted = useMounted()
  const authToken = useSessionStore((store) => store.authToken)
  const userQuery = useUserApi(!!authToken)
  const activeShipments = userQuery.data?.activeShipments ?? 0
  const signedIn = mounted && !!authToken
  const firstName = mounted ? userQuery.data?.firstName : undefined
  const openDialog = useModal((state) => state.openDialog)
  const queryClient = useQueryClient()

  const clearSession = useSessionStore((store) => store.clearSession)

  const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE')
    queryClient.clear()
    clearSession()
    window.location.reload()
  }

  const handleSignIn = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    openDialog('login-modal')
  }

  const userLinks = signedIn
    ? [
        ...brand.layoutContent.signedInUserLinks(activeShipments),
        {
          name: 'Log Out',
          onClick: handleLogout,
        },
      ]
    : [
        {
          name: 'Sign In',
          onClick: handleSignIn,
        },
      ]

  const accountText = signedIn && firstName ? `Hi, ${firstName}` : 'Sign In'

  return {
    type,
    brand: brand.key,
    logoLink: '/',
    brands: SITE_HEADER_BRANDS,
    links: SITE_HEADER_LINKS,
    helpLinks: SITE_HEADER_HELP_LINKS,
    userLinks,
    button: {
      name: SITE_HEADER_BUTTON.name,
      onClick: () => redirectFromBookingWidget(''),
    },
    accountText,
  }
}
